// BYOK rotate (#971) — the state half of the rotate modal, split from the two
// key containers the way `createWebhookTest` is split from the settings tab.
// AccountKeys and MyKeys share it verbatim (only the endpoint differs), so the
// 422 contract can't drift between the org and personal surfaces:
//
//   200  the masked row lands `valid` → close, toast, refetch (server truth,
//        no optimistic patch — the keys pages' existing posture).
//   422  the provider refused the NEW key and the OLD one is untouched and
//        still live — so the modal stays OPEN with the refusal inline
//        (`errorReason` precedence: problem `detail` first), never a close
//        that would read as "rotated".

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { errorReason } from './api';
import type { KeyRow } from './keys';

export type KeyRotateController = {
  /** The key being rotated, or null — drives the modal's open state. */
  readonly rotating: () => KeyRow | null;
  readonly busy: () => boolean;
  /** Inline refusal (422 `detail` / fallback copy), or null. */
  readonly error: () => string | null;
  /** Open the modal for a row; clears any refusal left from the last ask. */
  readonly open: (row: KeyRow) => void;
  readonly close: () => void;
  readonly submit: (input: { readonly key: string }) => void;
};

export function createKeyRotate(input: {
  /** `rotateKey` or `rotateMyKey`, token pre-bound by the container. */
  readonly rotate: (id: string, body: { readonly key: string }) => Promise<KeyRow>;
  readonly refetch: () => Promise<unknown>;
  readonly notify: (message: string) => void;
}): KeyRotateController {
  const [rotating, setRotating] = createSignal<KeyRow | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const run = async (body: { readonly key: string }): Promise<void> => {
    const row = rotating();
    if (!row || busy()) return;
    setBusy(true);
    setError(null);
    try {
      await input.rotate(row.id, body);
      setRotating(null);
      input.notify(t('account_keys.rotate_success_toast'));
      await input.refetch();
    } catch (e) {
      setError(errorReason(e, t('account_keys.rotate_failed')));
    } finally {
      setBusy(false);
    }
  };

  return {
    rotating,
    busy,
    error,
    open: (row) => {
      setError(null);
      setRotating(row);
    },
    close: () => setRotating(null),
    submit: (body) => void run(body),
  };
}
